import {
  DefaultContext,
  gql,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useRef } from 'react';
import { IRequest } from '../../services/graphql/GraphqlService';
import { getContextForRequest } from '../../shared/ApolloHooksUtils';

type CustomLazyQueryHookOptions<
  TData extends object = Record<string, unknown>,
> = Omit<useLazyQuery.Options<TData>, 'context' | 'variables'> & {
  context?: DefaultContext;
  variables?: OperationVariables;
};

const useCustomQuery = <TData extends object = Record<string, unknown>>(
  query: IRequest,
  queryHookOptions: useQuery.Options<TData> = {},
) => {
  const context = queryHookOptions.context ?? {};
  const document = gql(query.gql) as TypedDocumentNode<
    TData,
    OperationVariables
  >;

  return useQuery(document, {
    ...queryHookOptions,
    context: {
      ...context,
      ...getContextForRequest(query),
    },
  });
};

const useCustomMutation = <TData extends object = Record<string, unknown>>(
  mutation: IRequest,
  mutationHookOptions: useMutation.Options<TData> = {},
) => {
  const context = mutationHookOptions.context ?? {};
  const document = gql(mutation.gql) as TypedDocumentNode<
    TData,
    OperationVariables
  >;

  return useMutation(document, {
    ...mutationHookOptions,
    context: {
      ...context,
      ...getContextForRequest(mutation),
    },
  });
};

const useCustomLazyQuery = <TData extends object = Record<string, unknown>>(
  query: IRequest,
  lazyQueryHookOptions: CustomLazyQueryHookOptions<TData> = {},
) => {
  const {
    context: lazyQueryContext,
    variables: lazyQueryVariables,
    ...lazyQueryHookOptionsWithoutContext
  } = lazyQueryHookOptions;
  const document = gql(query.gql) as TypedDocumentNode<
    TData,
    OperationVariables
  >;

  const lazyQueryData = useLazyQuery(document, {
    ...lazyQueryHookOptionsWithoutContext,
  });

  const defaults = useRef({ lazyQueryContext, lazyQueryVariables, query });
  defaults.current = { lazyQueryContext, lazyQueryVariables, query };
  const executeQuery = lazyQueryData[0];
  const execute: typeof executeQuery = useCallback(
    (executeOptions = {}) =>
      executeQuery({
        ...executeOptions,
        variables: {
          ...defaults.current.lazyQueryVariables,
          ...executeOptions.variables,
        },
        context: {
          ...defaults.current.lazyQueryContext,
          ...executeOptions.context,
          ...getContextForRequest(defaults.current.query),
        },
      }),
    [executeQuery],
  );

  return [execute, lazyQueryData[1]] as typeof lazyQueryData;
};

export { useCustomQuery, useCustomMutation, useCustomLazyQuery };
